import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal component that animates its children when they enter the viewport.
 * Uses the high-performance Intersection Observer API.
 */
export default function ScrollReveal({
  children,
  className = '',
  variant = 'fade-up', // fade-up, fade-down, fade-left, fade-right, zoom-in
  duration = 700, // duration in ms
  delay = 0, // delay in ms
  threshold = 0.1, // trigger threshold
  once = true, // only animate once
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (once && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!once) {
          setIsIntersecting(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before entering fully
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [once, threshold]);

  // Define transition styles based on variants
  const getVariantStyles = () => {
    switch (variant) {
      case 'fade-up':
        return isIntersecting
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-12';
      case 'fade-down':
        return isIntersecting
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-12';
      case 'fade-left':
        return isIntersecting
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-12';
      case 'fade-right':
        return isIntersecting
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-12';
      case 'zoom-in':
        return isIntersecting
          ? 'opacity-100 scale-100'
          : 'opacity-0 scale-95';
      default:
        return isIntersecting ? 'opacity-100' : 'opacity-0';
    }
  };

  const style = {
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
  };

  return (
    <div
      ref={ref}
      className={`transition-all ease-out will-change-transform ${getVariantStyles()} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}
